/*eslint-disable*/
import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Chip,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from "@mui/material";
import { listAllLoginSessions, resetAllLoginSessions, fetchLoginSessionStats } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { tableStyle } from "../mui-styles/table";
import { PaginationFooter } from "../../components";
import { CalendarMonth, Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
} from "recharts";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import moment from "moment";

const LoginSessions = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const startInputRef = useRef();
    const endInputRef = useRef();

    const [startDate, setStartDate] = useState(0);
    const [endDate, setEndDate] = useState(0);
    const [source, setSource] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [chartView, setChartView] = useState("monthly");

    // Load aggregated stats for the chart (once on open).
    useEffect(() => {
        dispatch(fetchLoginSessionStats());
    }, [dispatch]);

    // Debounce only the search text so typing doesn't fire a request per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Fetch immediately on tab open and on date/source/(debounced)search changes.
    useEffect(() => {
        dispatch(resetAllLoginSessions());
        fetchMoreData();
        // eslint-disable-next-line
    }, [startDate, endDate, source, debouncedSearch]);

    const fetchMoreData = (page) => {
        dispatch(listAllLoginSessions({ ...page, startDate, endDate, source, search: debouncedSearch }));
    };

    const { loginSessionsList, pageNumber, pageSize, count } = useSelector(({ admin }) => {
        const loginSessions = admin?.loginSessions || {};
        return {
            loginSessionsList: loginSessions.list || [],
            pageNumber: loginSessions.pageNumber || 1,
            pageSize: loginSessions.pageSize || 50,
            count: loginSessions.count || 0,
        };
    });

    const { monthly, yearly, statsYear } = useSelector(({ admin }) => {
        const stats = admin?.loginSessionStats || {};
        return {
            monthly: stats.monthly || [],
            yearly: stats.yearly || [],
            statsYear: stats.year,
        };
    });

    const chartData = chartView === "monthly" ? monthly : yearly;
    const chartKey = chartView === "monthly" ? "month" : "year";

    const CustomFooter = () => (
        <PaginationFooter
            count={count}
            pageSize={pageSize}
            pageNumber={pageNumber}
            fetchMoreData={(page) => fetchMoreData(page)}
        />
    );

    const columns = [
        {
            field: "user_name",
            headerName: "User",
            width: 230,
            renderCell: ({ row: { user_name, user_email } }) => (
                <Stack>
                    <Typography color={colors.greenAccent[400]}>
                        <b>{user_name || "-"}</b>
                    </Typography>
                    <Typography fontSize={"small"}>{user_email || ""}</Typography>
                </Stack>
            ),
        },
        {
            field: "property_name",
            headerName: "Property",
            width: 220,
            renderCell: ({ row: { property_name } }) => property_name || "-",
        },
        {
            field: "source",
            headerName: "Source",
            width: 120,
            renderCell: ({ row: { source } }) => (
                <Chip
                    label={source === "mobile" ? "Mobile" : "Desktop"}
                    size="small"
                    sx={{
                        bgcolor: source === "mobile" ? colors.blueAccent[700] : colors.greenAccent[700],
                        color: "#fff",
                    }}
                />
            ),
        },
        {
            field: "login_time",
            headerName: "Login Time",
            width: 200,
            renderCell: ({ row: { login_time } }) =>
                login_time ? moment.unix(login_time).format("DD-MMM-YYYY - h:mm a") : "-",
        },
        {
            field: "logout_time",
            headerName: "Logout Time",
            width: 200,
            renderCell: ({ row: { logout_time } }) =>
                logout_time ? moment.unix(logout_time).format("DD-MMM-YYYY - h:mm a") : "-",
        },
    ];

    return (
        <Box m="20px" mb={15}>
            <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                m="20px 0"
            >
                <Header title="LOGIN SESSIONS" subtitle="Login activity across desktop & mobile" />
            </Box>

            {/* ── Stats chart ─────────────────────────────────────────────── */}
            <Box backgroundColor={colors.primary[400]} p="20px" borderRadius="8px" mb="20px">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                        <Typography variant="h5" fontWeight="600">
                            LOGIN ACTIVITY
                        </Typography>
                        <Typography variant="body2" color={colors.grey[300]}>
                            Desktop vs Mobile {chartView === "monthly" ? `· ${statsYear || ""}` : "· by year"}
                        </Typography>
                    </Box>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={chartView}
                        onChange={(e, v) => v && setChartView(v)}
                    >
                        <ToggleButton value="monthly">Monthly</ToggleButton>
                        <ToggleButton value="yearly">Yearly</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                        <XAxis dataKey={chartKey} tick={{ fill: colors.grey[100] }} />
                        <YAxis allowDecimals={false} tick={{ fill: colors.grey[100] }} />
                        <Tooltip
                            contentStyle={{ background: colors.primary[500], border: "none", borderRadius: 6 }}
                            labelStyle={{ color: colors.grey[100] }}
                        />
                        <Legend />
                        <Bar dataKey="web" name="Desktop" fill={colors.greenAccent[500]} radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="web" position="top" style={{ fill: colors.grey[100], fontSize: 11 }} />
                        </Bar>
                        <Bar dataKey="mobile" name="Mobile" fill={colors.blueAccent[400]} radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="mobile" position="top" style={{ fill: colors.grey[100], fontSize: 11 }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>

            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={1}>
                <Stack direction={"row"} spacing={1} flexGrow={1}>
                    <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        sx={{ width: 190 }}
                        value={startDate ? moment.unix(startDate).format("YYYY-MM-DD") : ""}
                        onChange={(e) => {
                            if (!e.target.value) {
                                setStartDate(0);
                                return;
                            }
                            setStartDate(moment(e.target.value, "YYYY-MM-DD").startOf("day").unix());
                        }}
                        inputRef={startInputRef}
                        onClick={() => startInputRef.current.showPicker()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonth sx={{ cursor: "pointer" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        sx={{ width: 190 }}
                        inputProps={{ min: startDate ? moment.unix(startDate).format("YYYY-MM-DD") : "" }}
                        value={endDate ? moment.unix(endDate).format("YYYY-MM-DD") : ""}
                        onChange={(e) => {
                            if (!e.target.value) {
                                setEndDate(0);
                                return;
                            }
                            setEndDate(moment(e.target.value, "YYYY-MM-DD").endOf("day").unix());
                        }}
                        inputRef={endInputRef}
                        onClick={() => endInputRef.current.showPicker()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonth sx={{ cursor: "pointer" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                <Stack direction={"row"} spacing={1}>
                    <TextField
                        select
                        size="small"
                        label="Source"
                        value={source || "all"}
                        sx={{ width: 160 }}
                        onChange={(e) => setSource(e.target.value === "all" ? "" : e.target.value)}
                    >
                        <MenuItem value={"all"}>All</MenuItem>
                        <MenuItem value={"web"}>Desktop</MenuItem>
                        <MenuItem value={"mobile"}>Mobile</MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        label="Search user / property"
                        sx={{ width: 250 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
            </Box>
            <Box m="8px 0 0 0" height="80vh" sx={tableStyle(colors)}>
                <DataGrid
                    rows={loginSessionsList}
                    columns={columns}
                    getRowId={(row) => row?._id}
                    components={{ Footer: CustomFooter }}
                />
            </Box>
        </Box>
    );
};

export default LoginSessions;
